/**
 * useScreenInsets — the safe-area a SCREEN still has to pay for itself.
 *
 * `useSafeAreaInsets()` answers a different question: "how much of the window
 * is physically unsafe?". Under a visible tab bar or the global mobile top bar
 * that area is already covered by the bar, so a screen that pads by
 * `insets.bottom` / `insets.top` pays it twice and produces a band of dead
 * background between its content and the bar.
 *
 * Use this hook for a screen's OWN layout — scroll content padding, sticky
 * footers, the fixed container edge. Keep `useSafeAreaInsets()` for anything
 * that escapes the tab layout and really does reach the window edge: modals,
 * full-screen overlays, bottom sheets.
 *
 * `left` and `right` pass through untouched: neither bar reserves the sides.
 */
import { useMemo } from 'react'
import { useSafeAreaInsets, type EdgeInsets } from 'react-native-safe-area-context'
import { useTabBarInset } from '../layout/tab-bar-inset'
import { useTopNavInset } from '../layout/top-nav-inset'

export function useScreenInsets(): EdgeInsets {
  const insets = useSafeAreaInsets()
  const reservedBottom = useTabBarInset()
  const reservedTop = useTopNavInset()

  return useMemo(
    () => ({
      top: Math.max(0, insets.top - reservedTop),
      left: insets.left,
      right: insets.right,
      bottom: Math.max(0, insets.bottom - reservedBottom),
    }),
    [insets.top, insets.left, insets.right, insets.bottom, reservedTop, reservedBottom],
  )
}
